import { Injectable, Injector } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { UserResourceToUserMapper } from '../models/mappers/user-resource-to-user.mapper';
import { UserResource } from '../models/resource/user.resource';
import { User } from '../models/ui/user.model';
import { AppConfigService } from '../services/app-config.service';
import { SdkBase } from '../utilities/sdk-base/sdk.base';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class UserSdk extends SdkBase {

	constructor(injector: Injector, private http: HttpClient, private appConfigService: AppConfigService) {
		super(injector);
	}

	public getUserDetails(userId: string): Observable<User> {
		return this.makeHttpRequest<UserResource>('get', `api2/users/${userId}`).pipe(
			map((response) => {
				const mapper = new UserResourceToUserMapper();
				return mapper.map(response);
			})
		);
	}

	public getUsersByUserType(userType: string): Observable<UserResource[]> {
		return this.makeHttpRequest<UserResource[]>('get', `api2/users?userType=${userType}`);
	}

	public getEmployeeUsers(): Observable<User[]> {
		return this.makeHttpRequest<UserResource[]>('get', `api2/users/employee`).pipe(
			map((response) => {
				const mapper = new UserResourceToUserMapper();
				return mapper.mapCollection(response);
			})
		);
	}

	public getActiveUsers(orderBy: [string, "asc" | "desc"], term?: string): Observable<User[]> {
		let query = '';
		if (term && term?.length > 0)
			query = `searchTerm=${term}&`
		return this.makeHttpRequest<UserResource[]>('get', `api2/users/active?${query}sortBy=${orderBy[0]}&sortOrder=${orderBy[1]}`).pipe(
			map((response) => {
				const mapper = new UserResourceToUserMapper();
				return mapper.mapCollection(response);
			})
		);
	}

	public getUserCurrentCase(userId: string): Observable<string> {
		return this.http.get<UserCasePermission>(`${this.appConfigService.getConfig().webApi}/api2/UserCasePermissions/${userId}`).pipe(
			map((response) => response.case?.caseName ?? '<Unknown>'),
			catchError(() => {
				return of('<Unknown>');
			}
			));
	}

	public patch(userId: string, userDelta: Partial<UserResource>): Observable<void> {
		const body = [];
		for (const key in userDelta) {
			body.push({
				op: "replace",
				path: `/${key}`,
				value: userDelta[key]
			})
		}
		return this.makeHttpRequest<void>('patch', `api2/users/${userId}`, body);
	}

	public delete(userId: string): Observable<void> {
		return this.makeHttpRequest<void>('delete', `api2/users/${userId}`);
	}
}

interface LexbeCaseBrief {
	caseId: string;
	accountId: string;
	caseName: string;
	docCount: number;
	dateTime?: string;
}

interface UserCasePermission {
	caseId: string;
	userId: string;
	isCurrentCase: boolean;
	createdById: string;
	dateTimeCreated: string;
	case: LexbeCaseBrief;
}
